// The "fly to explorer" animation: when Percy creates a resource, a ghost of its inline marker icon
// arcs from the thread to the Context Explorer toggle, drawing the eye to where the resource now
// lives. A position:fixed clone on <body> (viewport coords) survives the widget's floating / docked /
// fullscreen reparenting, where a framer-motion layoutId shared element would break or no-op.

// Unhurried enough to read as the resource "traveling" to the explorer, not just blinking there.
const FLY_MS = 760;
const PULSE_MS = 320;

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Fly a clone of the marker's icon to the toggle. Resolves when the flight ends (or immediately if
// it can't run), so the caller can time the badge pulse to the landing.
export const flyToExplorer = (markerEl: HTMLElement, toggleEl: HTMLElement): Promise<void> => {
  if (prefersReducedMotion()) return Promise.resolve();
  const iconEl = markerEl.querySelector<HTMLElement>('[data-resource-icon]') ?? markerEl;
  const from = iconEl.getBoundingClientRect();
  const to = toggleEl.getBoundingClientRect();
  // A detached / unrendered endpoint has a zero rect — nothing meaningful to animate between.
  if (from.width === 0 || to.width === 0) return Promise.resolve();

  const size = from.height;
  const startX = from.left;
  const startY = from.top;
  const endX = to.left + to.width / 2 - size / 2;
  const endY = to.top + to.height / 2 - size / 2;

  const ghost = document.createElement('div');
  ghost.setAttribute('aria-hidden', 'true');
  ghost.style.cssText = `position:fixed;left:0;top:0;width:${size}px;height:${size}px;z-index:60;pointer-events:none;`;
  const iconClone = iconEl.cloneNode(true) as HTMLElement;
  iconClone.style.width = `${size}px`;
  iconClone.style.height = `${size}px`;
  ghost.appendChild(iconClone);
  document.body.appendChild(ghost);

  const anim = ghost.animate(
    [
      { transform: `translate(${startX}px, ${startY}px) scale(1)`, opacity: 1 },
      // Lift over a shallow arc toward the toggle.
      {
        transform: `translate(${(startX + endX) / 2}px, ${Math.min(startY, endY) - 24}px) scale(0.9)`,
        opacity: 1,
        offset: 0.6,
      },
      { transform: `translate(${endX}px, ${endY}px) scale(0.35)`, opacity: 0 },
    ],
    { duration: FLY_MS, easing: 'cubic-bezier(0.5, 0, 0.2, 1)' },
  );
  return anim.finished
    .catch(() => {})
    .finally(() => ghost.remove())
    .then(() => {});
};

/** A quick scale bump on the count badge, to register a new arrival. */
export const pulseBadge = (badgeEl: HTMLElement) => {
  if (prefersReducedMotion()) return;
  badgeEl.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.4)' }, { transform: 'scale(1)' }], {
    duration: PULSE_MS,
    easing: 'ease-out',
  });
};
